/* ═══════════════════════════════════════════════════════════════════════
   NoteFlow — import-classify.ts
   Auto-classifier — guesses a section from account number ranges
   and account name pattern matching.
   ═══════════════════════════════════════════════════════════════════════ */

/**
 * Guess a financial-statement section from an account number and name.
 * Returns a section key (e.g. "current-assets", "revenue") or undefined
 * if no rule matched.
 */
export function autoClassify(acctNum: string | null | undefined, name: string | null | undefined): string | undefined {
  // Defensive: handle null/undefined inputs gracefully (CSV parsers can produce these)
  const safeName = (name ?? '').toString();
  const safeAcct = (acctNum ?? '').toString();
  const num = parseInt(safeAcct, 10);
  const n = safeName.toLowerCase();
  if (!n.trim() && isNaN(num)) return undefined;

  // ── Account number ranges (standard chart of accounts) ──
  if (!isNaN(num)) {
    if (num >= 1000 && num < 1500) return 'current-assets';
    if (num >= 1500 && num < 2000) return 'noncurrent-assets';
    if (num >= 2000 && num < 2500) return 'current-liab';
    if (num >= 2500 && num < 3000) return 'noncurrent-liab';
    if (num >= 3000 && num < 4000) return 'equity';
    if (num >= 4000 && num < 4100) return 'revenue';
    if (num >= 4100 && num < 4200) return 'revenue';     // returns & discounts still revenue
    if (num >= 4200 && num < 5000) return 'other';        // other income
    if (num >= 5000 && num < 6000) return 'cogs';
    if (num >= 6000 && num < 7000) return 'opex';
    if (num >= 7000 && num < 8000) return 'other';        // other expense
    if (num >= 8000 && num < 9000) return 'other';         // tax expense → other
    if (num >= 9000 && num < 10000) return 'opex';        // misc opex
  }

  // ── Name-based fallback patterns ──
  // Current assets
  if (/cash|petty cash|checking|savings/i.test(n)) return 'current-assets';
  if (/undeposited\s*funds/i.test(n)) return 'current-assets'; // QuickBooks default
  if (/receivable|interest rec|notes rec.*current/i.test(n)) return 'current-assets';
  if (/inventory|raw material|work.in.pro|wip|finished good/i.test(n)) return 'current-assets';
  // "deposit" matches security/lease/vendor deposits we paid (asset),
  // but EXCLUDES "customer deposit" / "client deposit" which are liabilities (we owe service)
  if (/prepaid|advance/i.test(n) && !/depreci/i.test(n)) return 'current-assets';
  if (/\bdeposit(?!ion)\b/i.test(n) && !/customer|client|received/i.test(n) && !/depreci/i.test(n)) return 'current-assets';
  if (/allowance.*doubtful|inventory reserve/i.test(n)) return 'current-assets';
  if (/short.term invest|marketable secur|trading secur/i.test(n)) return 'current-assets';
  // Tax recoverable / input VAT / HST recoverable (Canadian/international VAT systems)
  if (/vat\s*input|vat\s*recover|hst\s*recover|gst\s*recover|sales\s*tax\s*recover|tax\s*recoverable/i.test(n)) return 'current-assets';

  // Noncurrent assets
  if (/property|plant|equipment|land|building|machine|furniture|vehicle|leasehold|construction/i.test(n)) return 'noncurrent-assets';
  if (/accum.*depr|accum.*amort/i.test(n)) return 'noncurrent-assets';
  if (/intangible|patent|trademark|software|goodwill/i.test(n)) return 'noncurrent-assets';
  if (/right.of.use|rou|deferred tax asset|invest.*affil|long.term.*receiv/i.test(n)) return 'noncurrent-assets';

  // Noncurrent liabilities (checked first — more specific "long-term" / "noncurrent" prefixes/suffixes)
  if (/long.?term.*deferred\s*revenue|deferred\s*revenue.*long.?term|noncurrent.*deferred\s*revenue/i.test(n)) return 'noncurrent-liab';
  if (/long.term.*note|long.term.*loan|mortgage|bonds?\s*payable|bond\s*premium|capital lease|bank loan/i.test(n)) return 'noncurrent-liab';
  // Lease liability with noncurrent qualifier (any order)
  if (/lease.*(liab|obligation)/i.test(n) && /noncurrent|long.?term/i.test(n)) return 'noncurrent-liab';
  if (/(noncurrent|long.?term).*lease.*(liab|obligation)/i.test(n)) return 'noncurrent-liab';
  if (/deferred tax liab|pension|post.retirement|environmental.*liab|deferred rent/i.test(n)) return 'noncurrent-liab';

  // Current liabilities (checked BEFORE revenue so "Deferred Revenue" lands here, not in revenue)
  if (/deferred\s*revenue|unearned\s*revenue|customer\s*deposit|client\s*deposit|gift\s*card|refund\s*liab/i.test(n)) return 'current-liab';
  if (/accounts?\s*payable|accrued|income tax payable|sales tax|payroll.*with/i.test(n)) return 'current-liab';
  if (/short.term.*note|current portion|line.of.credit|unearned|dividend.*payable/i.test(n)) return 'current-liab';
  // Lease liability with current qualifier (any order) — also bare "lease liability" defaults to current
  if (/current.*lease.*(liab|obligation)|lease.*(liab|obligation).*current/i.test(n)) return 'current-liab';
  if (/lease\s*(liab|obligation)/i.test(n) && !/right.of.use/i.test(n)) return 'current-liab';
  // VAT/sales tax payable (output VAT)
  if (/vat\s*output|vat\s*payable|hst\s*payable|gst\s*payable/i.test(n)) return 'current-liab';
  // Bare "notes payable" (no time qualifier) defaults to current liability — common chart-of-accounts convention
  if (/notes?\s*payable|loan\s*payable/i.test(n)) return 'current-liab';

  // Equity (includes owner draws/distributions which are equity contra accounts)
  if (/common stock|preferred stock|paid.in capital|retained earnings|treasury|comprehensive|unrealized.*equity|share capital|capital stock|owner.*equity|partner.*capital/i.test(n)) return 'equity';
  if (/owner.*draw|owner.*distrib|member.*draw|partner.*draw|shareholder.*draw/i.test(n)) return 'equity';

  // Tax expense (checked BEFORE generic "expense" pattern so it classifies as 'other' not 'opex')
  if (/income\s*tax\s*exp|tax\s*expense|provision\s*for\s*(income\s*)?tax/i.test(n)) return 'other';

  // Revenue (after current-liab so "Deferred Revenue" doesn't match here)
  if (/revenue|sales(?!.*return)|subscription rev|licensing rev|consulting rev|maintenance.*rev/i.test(n)) return 'revenue';
  if (/sales return|sales discount/i.test(n)) return 'revenue';
  // SaaS metrics — Monthly/Annual Recurring Revenue (whole-word match to avoid false positives)
  if (/^(mrr|arr)\b/i.test(n) || /\b(mrr|arr)\b/i.test(n)) return 'revenue';

  // COGS
  if (/cost of goods|cost of service|cost of sub|cogs|freight.in/i.test(n)) return 'cogs';

  // Operating expenses
  if (/expense|salari|wage|compensation|bonus|commission|payroll tax|benefit|retirement|stock comp/i.test(n)) return 'opex';
  if (/rent\b|utilit|office suppl|postage|shipping|telephone|internet|equipment rental/i.test(n)) return 'opex';
  if (/depreciation exp|amortization exp/i.test(n)) return 'opex';
  if (/insurance exp|professional fee|legal|accounting|consulting fee|it service/i.test(n)) return 'opex';
  if (/advertis|market.*exp|trade show|website exp|public relation/i.test(n)) return 'opex';
  if (/travel|meal|entertainment|vehicle exp|parking|toll/i.test(n)) return 'opex';
  if (/repair|maintenance|janitor|landscap/i.test(n)) return 'opex';
  if (/bad debt|bank.*charge|credit card.*fee|licens.*permit|dues|subscript.*exp|training|recruit|charit|miscell/i.test(n)) return 'opex';
  if (/temporary labor/i.test(n)) return 'opex';

  // Other income/expense
  if (/interest income|dividend income|rental income|gain on|unrealized gain/i.test(n)) return 'other';
  if (/interest expense|bond interest|loss on|unrealized loss|impairment|restructur|litigation|foreign currency/i.test(n)) return 'other';
  if (/amortization of bond/i.test(n)) return 'other';
  if (/environmental.*expense/i.test(n)) return 'other';

  // Tax (classified under other income/expense)
  if (/income tax exp|tax expense/i.test(n)) return 'other';

  return undefined;
}
